import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { openMediaFromUrl, usePlaybackStore } from '@/modules/media/playbackStore';
import { useRoomStore } from './roomStore';

/**
 * Open whatever the room is watching.
 *
 * This is the piece that makes a room work when only one of the two people has
 * found the film. Without it, Watch Together assumes both sides independently
 * opened the same thing, which is a coordination problem solved over the phone
 * every single time.
 *
 * A local file is never overridden. If this machine already has the film open
 * from disk, that copy is better than a stream, and replacing it would be a
 * downgrade performed behind the user's back.
 */
export function useSharedSource(): void {
  const navigate = useNavigate();
  const source = useRoomStore((s) => s.source);
  const current = usePlaybackStore((s) => s.current);
  const open = usePlaybackStore((s) => s.open);

  // Remembered so that closing the player does not immediately reopen it,
  // which would make the close button appear broken.
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (source === null) {
      handled.current = null;
      return;
    }
    if (handled.current === source.url) return;
    if (current?.descriptor.url === source.url) {
      handled.current = source.url;
      return;
    }
    // A local copy beats a shared URL, and the fingerprint check will say if
    // the two are not the same film.
    if (current?.objectUrl !== null && current?.objectUrl !== undefined) {
      handled.current = source.url;
      return;
    }

    handled.current = source.url;
    open(openMediaFromUrl(source));
    void navigate('/watch');
  }, [source, current, open, navigate]);
}
